import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export interface PropertyCardData {
  id: number;
  title: string;
  slug: string;
  property_type: string;
  operation_type: string;
  price: number;
  currency: string;
  city: string;
  surface: number | null;
  rooms: number | null;
  bedrooms: number | null;
  cover_image_url: string | null;
  company_slug: string;
}

interface PropertyCardProps {
  property: PropertyCardData;
  companySlug?: string;
  isFavorite?: boolean;
  onToggleFavorite?: (propertyId: number) => void;
  showFavorite?: boolean;
}

function formatPrice(price: number, currency: string): string {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
}

export default function PropertyCard({
  property,
  companySlug,
  isFavorite = false,
  onToggleFavorite,
  showFavorite = false,
}: PropertyCardProps): React.ReactElement {
  const { t } = useTranslation('catalog');
  const navigate = useNavigate();

  const resolvedCompanySlug = companySlug || property.company_slug;

  const handleClick = (): void => {
    navigate(`/c/${resolvedCompanySlug}/${property.slug}`);
  };

  const handleFavoriteClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
    onToggleFavorite?.(property.id);
  };

  const propertyTypeLabel = t(`propertyTypes.${property.property_type}`);
  const operationTypeLabel = t(`operationTypes.${property.operation_type}`);

  return (
    <div className="card card-compact bg-base-100 shadow-md h-full relative cursor-pointer hover:shadow-lg transition-shadow">
      {showFavorite && onToggleFavorite && (
        <button
          onClick={handleFavoriteClick}
          className="btn btn-circle btn-sm btn-ghost bg-white/85 hover:bg-white/95 absolute top-2 right-2 z-10"
          aria-label={isFavorite ? t('favorites.remove') : t('favorites.add')}
        >
          {isFavorite ? (
            <span className="text-red-500 text-lg">&#9829;</span>
          ) : (
            <span className="text-gray-500 text-lg">&#9825;</span>
          )}
        </button>
      )}

      <div onClick={handleClick} className="flex flex-col h-full">
        <figure>
          {property.cover_image_url ? (
            <img
              src={property.cover_image_url}
              alt={property.title}
              className="w-full h-[200px] object-cover"
            />
          ) : (
            <div className="w-full h-[200px] flex items-center justify-center bg-gray-100">
              <span className="text-6xl text-gray-400">&#8962;</span>
            </div>
          )}
        </figure>

        <div className="card-body flex-grow flex flex-col">
          <h2 className="card-title text-base font-semibold truncate">
            {property.title}
          </h2>

          <div className="flex flex-row gap-2 mb-1">
            <span className="badge badge-outline badge-primary badge-sm">{propertyTypeLabel}</span>
            <span className="badge badge-outline badge-secondary badge-sm">{operationTypeLabel}</span>
          </div>

          <p className="text-lg font-bold text-primary mb-1">
            {formatPrice(property.price, property.currency)}
          </p>

          <p className="text-sm text-base-content/60 mb-1">
            {property.city}
          </p>

          <div className="flex flex-row gap-4 mt-auto">
            {property.surface != null && (
              <div className="flex items-center gap-1">
                <span className="text-base-content/40 text-sm">&#9633;</span>
                <span className="text-sm text-base-content/60">
                  {property.surface} {t('property.sqm')}
                </span>
              </div>
            )}
            {property.rooms != null && (
              <div className="flex items-center gap-1">
                <span className="text-base-content/40 text-sm">&#8962;</span>
                <span className="text-sm text-base-content/60">
                  {property.rooms} {t('property.rooms')}
                </span>
              </div>
            )}
            {property.bedrooms != null && (
              <div className="flex items-center gap-1">
                <span className="text-base-content/40 text-sm">&#9738;</span>
                <span className="text-sm text-base-content/60">
                  {property.bedrooms}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
